import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Gift, Share2, Copy, Check, Users, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

/**
 * Tiny navbar chip — "🎁 Earn free months" — that opens a dialog with the
 * user's referral link + share/copy buttons. Visible from every authenticated
 * page so the share action is one tap from anywhere.
 *
 * Fetches stats lazily (only when dialog opens) to avoid extra API calls on
 * every page load.
 */
export default function ShareReferralChip({ className = '' }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || data) return;
    setLoading(true);
    api.get('/me/referral-stats')
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, data]);

  const handleShare = async () => {
    if (!data?.share_link) return;
    const shareText = `I'm using Diamond Scorer to score our team's games + keep parents in the loop — way easier than SportsEngine on game day. Use my link and your free trial is 30 days (instead of 7): ${data.share_link}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Diamond Scorer', text: shareText, url: data.share_link });
        return;
      } catch { /* user canceled */ }
    }
    handleCopy(shareText);
  };

  const handleCopy = async (text) => {
    const toCopy = text || data?.share_link || '';
    if (!toCopy) return;
    try {
      await navigator.clipboard.writeText(toCopy);
      setCopied(true);
      toast.success('Copied! Paste it in a text or group chat.');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — long-press to select');
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider transition-all shadow-sm hover:shadow-md ${className}`}
        style={{
          background: 'linear-gradient(135deg, #F5C455 0%, #D4A03A 100%)',
          color: '#0A1322',
        }}
        data-testid="share-referral-chip"
        aria-label="Share Diamond Scorer and earn free months"
      >
        <Gift size={12} />
        <span className="hidden sm:inline">Earn free months</span>
        <span className="sm:hidden">Share</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="share-referral-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="text-amber-500" size={20} />
              Give 30 days, get 30 days
            </DialogTitle>
            <DialogDescription>
              Share your link. Coaches who sign up get a <b>30-day trial</b> (instead of 7).
              When they pay, you get <b>+30 days free</b>.
            </DialogDescription>
          </DialogHeader>

          {loading && (
            <div className="flex items-center justify-center py-6 text-stone-500 gap-2" data-testid="share-dialog-loading">
              <Loader2 size={16} className="animate-spin" /> Generating your link…
            </div>
          )}

          {data && (
            <div className="space-y-3">
              {/* Stats — only show if any activity */}
              {(data.invited_count > 0 || data.days_earned > 0) && (
                <div className="grid grid-cols-3 gap-2" data-testid="share-dialog-stats">
                  <div className="bg-stone-100 rounded-lg p-2 text-center">
                    <div className="text-xl font-extrabold text-stone-900">{data.invited_count || 0}</div>
                    <div className="text-[10px] uppercase tracking-wider text-stone-500">Invited</div>
                  </div>
                  <div className="bg-stone-100 rounded-lg p-2 text-center">
                    <div className="text-xl font-extrabold text-stone-900">{data.paid_count || 0}</div>
                    <div className="text-[10px] uppercase tracking-wider text-stone-500">Paid</div>
                  </div>
                  <div className="bg-amber-400 rounded-lg p-2 text-center">
                    <div className="text-xl font-extrabold text-stone-900">+{data.days_earned || 0}d</div>
                    <div className="text-[10px] uppercase tracking-wider text-stone-900">Earned</div>
                  </div>
                </div>
              )}

              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-stone-500 mb-1">Your share link</div>
                <div className="bg-stone-100 rounded-lg p-2 flex items-center gap-2">
                  <code className="text-[11px] md:text-sm font-mono text-stone-900 truncate flex-1" data-testid="share-dialog-link">
                    {data.share_link}
                  </code>
                  <button
                    onClick={() => handleCopy()}
                    className="shrink-0 w-8 h-8 rounded bg-white hover:bg-amber-50 border border-stone-300 flex items-center justify-center transition"
                    aria-label="Copy link"
                    data-testid="share-dialog-copy-btn"
                  >
                    {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                  </button>
                </div>
              </div>

              <Button
                onClick={handleShare}
                className="w-full bg-amber-500 hover:bg-amber-400 text-stone-900 font-bold h-11"
                data-testid="share-dialog-share-btn"
              >
                <Share2 size={14} className="mr-2" /> Share my link
              </Button>

              {data.invited_count === 0 && (
                <p className="text-[11px] text-stone-500 text-center italic">
                  <Users size={11} className="inline mr-1" /> First coach who joins via your link unlocks +30 days for you.
                </p>
              )}
            </div>
          )}

          {!loading && !data && (
            <p className="text-stone-500 text-sm text-center py-4" data-testid="share-dialog-error">
              Couldn't load your link. Please refresh and try again.
            </p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
